/**
 * PodFrameColorsWhitelistSelector — whitelist FrameColor Gelato +
 * self-service provisioning (Plan B Palier 3, 2026-05-18).
 *
 * Stéphanie coche les couleurs de cadre Gelato qu'elle propose. Au save,
 * le BE orchestre en lot :
 *   - Auto-create framings catalog `gelato-<color>`.
 *   - Auto-create printable_specs Gelato (1 parent par color + variants
 *     enfants = dimensions whitelistées).
 *   - Auto-set le mapping `pod_matrix_default_spec_by_finish` atelier.
 *   - Soft-archive les framings/specs orphelins (colors dé-cochées).
 *
 * Synchrone, ~5-30s. Toast post-save avec résumé du provisioning.
 */

import { useState } from 'react'
import { toast } from 'sonner'

import Button from '@/components/ui/Button'
import Checkbox from '@/components/ui/Checkbox'
import {
  usePodFrameColorsWhitelist,
  useUpdatePodFrameColorsWhitelist,
} from '@/hooks/useAtelierSettings'

import './PodFrameColorsWhitelistSelector.css'

// Labels FR pour les 4 colors Gelato (miroir _GELATO_COLOR_META côté BE).
const COLOR_LABELS: Record<string, { fr: string; hex: string }> = {
  black: { fr: 'Cadre bois noir', hex: '#1A1A1A' },
  white: { fr: 'Cadre bois blanc', hex: '#F5F5F5' },
  'natural-wood': { fr: 'Cadre bois naturel', hex: '#C9A77E' },
  'dark-wood': { fr: 'Cadre bois foncé', hex: '#5C3A20' },
}

export default function PodFrameColorsWhitelistSelector() {
  const settingQuery = usePodFrameColorsWhitelist()
  const updateMutation = useUpdatePodFrameColorsWhitelist()

  const serverData = settingQuery.data
  const available = serverData?.available ?? []

  const computeInitial = (): Set<string> => {
    if (!serverData) return new Set()
    // Liste vide côté serveur → propose tout dé-coché (Stéphanie choisit).
    return new Set(serverData.value)
  }

  const [selected, setSelected] = useState<Set<string>>(computeInitial)
  const [includeNoFrame, setIncludeNoFrame] = useState(true)
  const [serverSnapshot, setServerSnapshot] = useState<string | null>(
    serverData ? JSON.stringify(serverData.value) : null,
  )
  if (serverData) {
    const next = JSON.stringify(serverData.value)
    if (next !== serverSnapshot) {
      setServerSnapshot(next)
      setSelected(computeInitial())
    }
  }

  const toggle = (color: string) => {
    const next = new Set(selected)
    if (next.has(color)) {
      next.delete(color)
    } else {
      next.add(color)
    }
    setSelected(next)
  }

  const handleSave = () => {
    updateMutation.mutate(
      {
        value: Array.from(selected),
        include_no_frame: includeNoFrame,
      },
      {
        onSuccess: (summary) => {
          const parts: string[] = []
          if (summary.framings_created.length > 0) {
            parts.push(`${summary.framings_created.length} encadrement(s) créé(s)`)
          }
          if (summary.specs_created.length > 0) {
            parts.push(`${summary.specs_created.length} spec(s) créée(s)`)
          }
          if (summary.variants_added > 0) {
            parts.push(`${summary.variants_added} variante(s) ajoutée(s)`)
          }
          if (summary.framings_archived.length > 0) {
            parts.push(`${summary.framings_archived.length} archivé(s)`)
          }
          if (summary.skipped.length > 0) {
            parts.push(`${summary.skipped.length} skip (productUid invalide)`)
          }
          toast.success(
            parts.length > 0
              ? `Provisioning POD réussi : ${parts.join(' · ')}`
              : 'Whitelist sauvegardée (rien à provisionner).',
          )
        },
        onError: (err) => {
          const detail = (err.detail as string | undefined) ?? 'Provisioning échoué.'
          toast.error(detail)
        },
      },
    )
  }

  const isLoading = settingQuery.isLoading
  // Le re-provisionning est idempotent côté BE — on n'enferme PAS le
  // bouton derrière un `isDirty` strict. Si Stéphanie a ajouté une
  // dimension à la whitelist sans toucher aux couleurs, elle doit pouvoir
  // re-déclencher le provisioning pour ajouter les variants manquants.
  const hasAtLeastOneColor = selected.size > 0
  const isDirty =
    serverData &&
    JSON.stringify(Array.from(selected).sort()) !== JSON.stringify([...serverData.value].sort())

  return (
    <section className="pod-frame-colors-whitelist-selector">
      <header className="pod-frame-colors-whitelist-header">
        <h3 className="ui-h5">Couleurs de cadre Gelato</h3>
        <p className="ui-body-sm ui-text-muted">
          Coche les couleurs proposées par Gelato (catalog fine art framed poster). Le save
          auto-crée les encadrements et les specs Gelato pour{' '}
          <strong>chaque dimension whitelistée</strong> ci-dessus, et configure le mapping atelier.
          Synchrone, ~5-30s.
        </p>
      </header>

      {isLoading ? (
        <p className="ui-body ui-text-muted" role="status">
          Chargement…
        </p>
      ) : (
        <>
          <ul className="pod-frame-colors-whitelist-grid">
            {available.map((color) => {
              const meta = COLOR_LABELS[color] ?? { fr: color, hex: '#888' }
              const isChecked = selected.has(color)
              return (
                <li
                  key={color}
                  className={
                    'pod-frame-colors-whitelist-row' +
                    (isChecked ? ' pod-frame-colors-whitelist-row--checked' : '')
                  }
                >
                  <Checkbox
                    checked={isChecked}
                    onChange={() => toggle(color)}
                    aria-label={meta.fr}
                  />
                  <span
                    className="pod-frame-colors-whitelist-swatch"
                    style={{ backgroundColor: meta.hex }}
                    aria-hidden="true"
                  />
                  <div className="pod-frame-colors-whitelist-label">
                    <strong>{meta.fr}</strong>
                    <code className="ui-body-sm ui-text-muted">{color}</code>
                  </div>
                </li>
              )
            })}
          </ul>

          <label className="pod-frame-colors-whitelist-noframe">
            <Checkbox
              checked={includeNoFrame}
              onChange={() => setIncludeNoFrame(!includeNoFrame)}
            />
            <span>
              Inclure <strong>« Sans cadre »</strong> (poster fine-art simple) dans le mapping
              atelier.
            </span>
          </label>

          <div className="pod-frame-colors-whitelist-actions">
            <Button
              variant="primary"
              size="md"
              onClick={handleSave}
              disabled={!hasAtLeastOneColor || updateMutation.isPending}
            >
              {updateMutation.isPending
                ? 'Provisioning en cours (~30s)…'
                : isDirty
                  ? 'Provisionner'
                  : 'Re-provisionner (idempotent)'}
            </Button>
          </div>
        </>
      )}
    </section>
  )
}
